import { DatabaseBackup as DatabaseBackupIcon } from 'iconoir-react'
import moment from 'moment'
import { ReactElement, useEffect, useState } from 'react'
import { getName } from 'app/components/LazyStaffCell'
import { ActivityCard } from 'app/components/activities/ActivityCard'
import type { ActivityJson } from 'app/types/entities'
import { useUser } from 'app/utils'
import { bg } from 'lib/background'
import { changeAuthor, changeLines } from 'lib/changeSummary'
import type { ChangeLine } from 'lib/changeSummary'
import { logError } from 'lib/logging'

type UpdateRow = {
  key: string
  date?: string | null
  source?: string
  line?: ChangeLine
  /** For records that are an event rather than a field change, e.g. Patient. */
  text?: string
}

/**
 * Every field change a contact's update records describe, flattened.
 *
 * One activity can carry several fields, so five update records are usually
 * more than five facts. The card counts and lists the facts, not the records,
 * because "4 updates" meaning four database writes of unknown size tells the
 * reader nothing.
 */
function updateRows(activities: ActivityJson[]): UpdateRow[] {
  const rows: UpdateRow[] = []

  for (const activity of activities) {
    if (activity.type !== `Change`) {
      rows.push({
        key: activity.id ?? `${activity.activityDate}`,
        date: activity.activityDate,
        source: activity.source,
        text: activity.description ?? activity.type ?? `Update`,
      })
      continue
    }

    try {
      const data = activity.data || JSON.parse(activity.description!)
      for (const line of changeLines(data)) {
        rows.push({ key: `${activity.id}-${line.field}`, date: activity.activityDate, source: activity.source, line })
      }
    } catch (err) {
      logError(`could not read change activity`, activity.id, err)
    }
  }

  return rows
}

/**
 * "Open to Closed Won", or "set to Patient" where nothing preceded it.
 *
 * The connector matters: a bare value beside the field name reads as part of
 * the field. "Lifecycle Stage New Patient Scheduled" is four words that could
 * be parsed several ways; "Lifecycle Stage set to New Patient Scheduled" only
 * one.
 */
function transitionText(line: ChangeLine): string {
  if (line.kind === `statement`) return line.text
  if (!line.from) return `set to ${line.to || `empty`}`
  return `${line.from} to ${line.to || `empty`}`
}

/**
 * " by Patrick", resolving a user id to a name. Renders nothing until a name is
 * known, so a row never flashes a raw id.
 */
export function ChangeAuthorName({ source }: { source?: string }): ReactElement | null {
  const user = useUser()
  const author = changeAuthor(source)
  const userId = author && `userId` in author ? author.userId : undefined
  const [userName, setUserName] = useState<string>()

  useEffect(() => {
    if (!userId || userId === user?.id) return
    bg(getName(userId).then(setUserName))
  }, [userId, user?.id])

  const name = !author ? undefined : `label` in author ? author.label : userId === user?.id ? `you` : userName
  if (!name || name === `Unknown`) return null
  return <span className="text-minimal">{` by ${name === user?.name ? `you` : name}`}</span>
}

/**
 * A month's contact updates as one card.
 *
 * These are bookkeeping: a lifecycle stage moving, an owner being assigned, a
 * patient record linking. Individually each is a card with a chevron hiding one
 * sentence, and a contact accumulates them steadily, so a month of them used to
 * push the things people actually came to read off the screen. Grouped, the
 * whole month costs one card and every fact stays visible on it.
 */
export function UpdateRun({ activities }: { activities: ActivityJson[] }): ReactElement {
  const rows = updateRows(activities)

  return (
    <ActivityCard
      icon={<DatabaseBackupIcon className="h-5 w-5 shrink-0 rounded-md bg-inverse p-1 text-inverse" />}
      hideChevronIcon
      title={
        <div className="flex flex-wrap items-center gap-x-1 text-ui-sm text-strong">
          <span>{`${rows.length} ${rows.length === 1 ? `update` : `updates`}`}</span>
        </div>
      }
      preview={
        <div className="w-full rounded-lg border border-default bg-surface px-4 py-3 text-body-xs shadow-sm">
          <ul className="flex flex-col">
            {rows.map((row) => (
              <li
                key={row.key}
                className="flex items-baseline justify-between gap-x-3 border-b border-subtle py-2 first:pt-0 last:border-b-0 last:pb-0"
              >
                <span className="min-w-0 text-body-xs text-subtle">
                  {row.line ? (
                    <>
                      <span className="text-strong">{row.line.field}</span> {transitionText(row.line)}
                    </>
                  ) : (
                    row.text
                  )}
                  <ChangeAuthorName source={row.source} />
                </span>
                <span className="shrink-0 text-body-xs text-minimal">
                  {/*
                    UTC, like the feed's own month grouping. These records are
                    stamped at midnight UTC to mean a calendar day, so reading
                    them locally slides every one of them a day backwards for
                    anyone west of UTC.
                  */}
                  {row.date ? moment.utc(row.date).format(`MMM D`) : ``}
                </span>
              </li>
            ))}
          </ul>
        </div>
      }
      isFirst
      isLast
    />
  )
}
